import React, { Component } from 'react';
import { get, post, userAction } from '@/util/helpers/ajax-helpers';
import {
  addItemToArray,
  removeItemFromArray,
} from '@/util/helpers/array-helpers';
import style from '../css/module/team.css';
import flex from '../css/module/flex.css';
import AjaxSpinner from '../components/AjaxSpinner';
import SearchInput from '../components/SearchInput';
import InviteCollaborators from '../components/InviteCollaborators';
import { ADMIN, COLLABORATOR } from '../constants';

class TeamAccess extends Component {
  state = {
    loading: true,
    searchQuery: '',
    saving: false, // true while sending any change to server
    selfHosted: false, // true if this app is self hosted
    invitationsSent: false, // invitations successfully sent
    emailToRemove: null, // email of user to be removed
    emails: null, // array of emails loaded from server
    admins: null, // array of admin emails loaded from server (each one is also contained in the emails state array)
    ownerEmail: null, // email of owner
    ownerIsHerokuAddon: null, // bool
  };

  componentDidMount() {
    this.loadCollaborators();
  }

  loadCollaborators(_invitationsSent = false) {
    get('loading team members', '/collaborators')
      .then((json) => {
        const collabs = json._collaborators
          .map((email) => {
            return email;
          })
          .sort((a, b) => {
            return a.localeCompare(b);
          });
        this.setState(({ invitationsSent }) => ({
          emails: collabs,
          admins: json._admins.map((email) => email),
          ownerEmail: json._ownerEmail,
          ownerIsHerokuAddon: json._ownerIsHerokuAddon,
          loading: false,
          invitationsSent: _invitationsSent || invitationsSent, // either true or should retain existing value
          selfHosted: json._selfHosted,
          saving: false,
        }));
      })
      .catch(() => {
        this.setState({
          saving: false,
        });
      });
  }

  saveCollaborators = (newEmailsArray) => {
    const { emails, admins } = this.state;
    const newCollaborators = newEmailsArray
      .filter((obj) => emails.indexOf(obj.emailAddress) === -1)
      .map((obj) => obj.emailAddress);
    const newAdmins = newEmailsArray
      .filter(
        (obj) => emails.indexOf(obj.emailAddress) === -1 && obj.role === ADMIN,
      )
      .map((obj) => obj.emailAddress);

    const allCollaborators = [...emails, ...newCollaborators];
    const allAdmins = [...admins, ...newAdmins].filter(
      (email) => allCollaborators.indexOf(email) !== -1,
    );
    this.sendToServer(allCollaborators, allAdmins, true);

    userAction('click-invite-datasource-member', {
      all_invites: newCollaborators,
      new_admins: newAdmins,
    });
  };

  updateRole(email, newRole, previousRole) {
    const { emails, admins } = this.state;
    const allAdmins =
      newRole === COLLABORATOR
        ? removeItemFromArray(admins, email) // remove email from admins array
        : addItemToArray(admins, email); // add email to admins array;
    this.sendToServer(emails, allAdmins);
    userAction('update-datasource-member-role', {
      email,
      new_role: newRole,
      old_role: previousRole,
    });
  }

  sendToServer(allCollaborators, allAdmins, invitesSent) {
    this.setState({
      saving: true,
    });
    post('saving team members', '/collaborators', {
      _emails: allCollaborators,
      _adminEmails: allAdmins,
    })
      .then(() => {
        this.loadCollaborators(invitesSent);
      })
      .catch(() => {
        this.setState({
          saving: false,
        });
      });
  }

  roleSelectMenu(email) {
    const { admins, saving } = this.state;
    const role = admins.includes(email) ? ADMIN : COLLABORATOR;
    return (
      <select
        disabled={saving}
        value={role}
        onChange={(e) => this.updateRole(email, e.target.value, role)}
      >
        <option value={COLLABORATOR}>{COLLABORATOR}</option>
        <option value={ADMIN}>{ADMIN}</option>
      </select>
    );
  }

  deleteUser(email) {
    const { emails, admins } = this.state;
    const allCollaborators = removeItemFromArray(emails, email);
    const isAdmin = admins.indexOf(email) !== -1;
    const allAdmins = isAdmin ? removeItemFromArray(admins, email) : admins;
    this.sendToServer(allCollaborators, allAdmins);
    userAction('click-remove-datasource-member', {
      email,
      role: isAdmin ? ADMIN : COLLABORATOR,
    });
  }

  render() {
    const { loading, emails } = this.state;

    if (loading) {
      return (
        <div
          className={`${style.mainContainer} w-100 h-100 ${flex.flex} ${flex.alignItemsCenter} ${flex.justifyContentCenter}`}
        >
          <AjaxSpinner />
        </div>
      );
    }

    return (
      <div className={style.pageContainer}>
        <div className={style.pageContent}>
          <div className={style.title}>
            <h1>Manage team</h1>
          </div>
          <div className={style.mainContent}>
            <InviteCollaborators
              saving={this.state.saving}
              startAgain={() => this.setState({ invitationsSent: false })}
              existingEmails={emails}
              invitationsSent={this.state.invitationsSent}
              onClickSubmit={this.saveCollaborators}
              selfHosted={this.state.selfHosted}
            />
            <div className="w-100">
              {emails.length < 6 ? null : (
                <div className={`${flex.justifyContentEnd} margin-bottom-20`}>
                  <SearchInput
                    value={this.state.searchQuery}
                    onSearch={(value) => this.setState({ searchQuery: value })}
                    placeholder="Search users"
                    style={{ borderBottom: '1px solid #ddd' }}
                  />
                </div>
              )}
              <table className="widget-table">
                <thead>
                  <tr className={`widget-headers ${style.headers}`}>
                    <td>Existing members</td>
                    <td />
                    <td>Access level</td>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {!this.state.ownerIsHerokuAddon
                        ? this.state.ownerEmail
                        : 'Add-on (via Heroku)'}
                    </td>
                    <td />
                    <td>Owner</td>
                  </tr>
                  {emails
                    .filter(
                      (email) =>
                        this.state.searchQuery.length < 3 ||
                        email.includes(this.state.searchQuery),
                    )
                    .map((email, i) => {
                      return (
                        <tr key={i}>
                          <td>{email}</td>
                          <td>
                            {email === this.state.emailToRemove ? (
                              <div className={flex.justifyContentEnd}>
                                <span
                                  onClick={() =>
                                    this.setState({ emailToRemove: null })
                                  }
                                  className={style.cancelUserDelete}
                                >
                                  Cancel
                                </span>
                                &nbsp; &nbsp;
                                <span
                                  onClick={() => this.deleteUser(email)}
                                  className={style.confirmUserDelete}
                                >
                                  Remove access
                                </span>
                              </div>
                            ) : (
                              <span
                                onClick={() =>
                                  this.setState({ emailToRemove: email })
                                }
                                className={style.deleteUserCell}
                              >
                                Remove
                              </span>
                            )}
                          </td>
                          <td>{this.roleSelectMenu(email)}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default TeamAccess;
